import type { Renderer, RendererHooks, DragOverlay } from '../renderer';
import { createProjection, composeCamera, SAFE_INSET_CSS, type Projection } from '../projection';
import type { AppState } from '../../state/default-state';
import { effectiveWorldPosition } from '../../state/phase';
import { getBody } from '../../core/reference-data';
import { orbitEllipse, maxApoapsis } from '../../core/orbit-geometry';
import { buildSectors, sectorFillClass } from '../../core/sectors';
import { markerRadiusPx, REALISTIC_LOCATOR_RADIUS_CSS } from '../body-scale';
import type { Body, BodyId, BodyStatus } from '../../core/types';

// DESIGN.md tokens used by the baseline renderer.
const SURFACE_LOWEST = '#0c0e12';
const OUTLINE_VARIANT = '#414751';
const ON_SURFACE = '#e1e2e7';
// Primary family (DESIGN.md / global.css) for the equal-time-sector overlay: two
// low-opacity translucent fills that alternate on adjacent sectors so the wedges
// read as distinct without relying on colour alone (the shape difference and the
// boundary strokes carry the meaning too).
const PRIMARY = '#a4c9ff';
const PRIMARY_CONTAINER = '#4d93e5';
// Error family (DESIGN.md, reserved for collision/absorption). Central absorption
// (Story 4.2) draws its distinct implosion in this colour so the learner tells it
// apart from a body-body collision's identity-coloured outward burst.
const ERROR = '#ff5449';

export type PlanetIdentityMark =
  | 'plain'
  | 'crater'
  | 'cloud-band'
  | 'land-patch'
  | 'dark-patch'
  | 'bands'
  | 'ringed-bands'
  | 'tilted-band'
  | 'storm-spot';

export interface PlanetIdentityStyle {
  readonly mark: PlanetIdentityMark;
  readonly bandCount: number;
  readonly spotCount: number;
  readonly bandTiltRadians: number;
  readonly ringTiltRadians: number;
  readonly ringRadiusX: number;
  readonly ringRadiusY: number;
  readonly markAlpha: number;
  /**
   * Ring opacity, kept separate from `markAlpha` so a body can carry its band/spot
   * marks at one strength while its ring reads at another. Saturn's rings are bold;
   * the other giants (Jupiter, Uranus, Neptune) have real but faint rings, so they
   * get a subtle `ringAlpha` while keeping their own surface marks.
   */
  readonly ringAlpha: number;
}

const PLAIN_PLANET_IDENTITY_STYLE: PlanetIdentityStyle = {
  mark: 'plain',
  bandCount: 0,
  spotCount: 0,
  bandTiltRadians: 0,
  ringTiltRadians: 0,
  ringRadiusX: 0,
  ringRadiusY: 0,
  markAlpha: 0,
  ringAlpha: 0
};

const PLANET_IDENTITY_STYLES: Readonly<Record<BodyId, PlanetIdentityStyle>> = {
  sun: PLAIN_PLANET_IDENTITY_STYLE,
  mercury: {
    mark: 'crater',
    bandCount: 0,
    spotCount: 3,
    bandTiltRadians: 0,
    ringTiltRadians: 0,
    ringRadiusX: 0,
    ringRadiusY: 0,
    markAlpha: 0.34,
    ringAlpha: 0
  },
  venus: {
    mark: 'cloud-band',
    bandCount: 2,
    spotCount: 0,
    bandTiltRadians: -0.18,
    ringTiltRadians: 0,
    ringRadiusX: 0,
    ringRadiusY: 0,
    markAlpha: 0.38,
    ringAlpha: 0
  },
  earth: {
    mark: 'land-patch',
    bandCount: 0,
    spotCount: 2,
    bandTiltRadians: 0,
    ringTiltRadians: 0,
    ringRadiusX: 0,
    ringRadiusY: 0,
    markAlpha: 0.42,
    ringAlpha: 0
  },
  mars: {
    mark: 'dark-patch',
    bandCount: 0,
    spotCount: 2,
    bandTiltRadians: 0,
    ringTiltRadians: 0,
    ringRadiusX: 0,
    ringRadiusY: 0,
    markAlpha: 0.36,
    ringAlpha: 0
  },
  jupiter: {
    // Jupiter keeps its banded surface and gains a faint equatorial ring (its real
    // ring system is thin and dark, so ringAlpha is low).
    mark: 'bands',
    bandCount: 4,
    spotCount: 1,
    bandTiltRadians: 0.04,
    ringTiltRadians: 0.05,
    ringRadiusX: 1.5,
    ringRadiusY: 0.3,
    markAlpha: 0.44,
    ringAlpha: 0.22
  },
  saturn: {
    // Saturn's rings stay the bold, iconic treatment (widest radius, strongest alpha).
    mark: 'ringed-bands',
    bandCount: 3,
    spotCount: 0,
    bandTiltRadians: 0.03,
    ringTiltRadians: -0.22,
    ringRadiusX: 1.85,
    ringRadiusY: 0.52,
    markAlpha: 0.46,
    ringAlpha: 0.46
  },
  uranus: {
    // Uranus's real rings are near-vertical (its ~98 deg axial tilt); the steep
    // ringTilt is its signature identity cue, kept subtle.
    mark: 'tilted-band',
    bandCount: 1,
    spotCount: 0,
    bandTiltRadians: 0.72,
    ringTiltRadians: 1.3,
    ringRadiusX: 1.55,
    ringRadiusY: 0.4,
    markAlpha: 0.34,
    ringAlpha: 0.26
  },
  neptune: {
    // Neptune keeps its storm spot and gains a faint tilted ring.
    mark: 'storm-spot',
    bandCount: 1,
    spotCount: 1,
    bandTiltRadians: -0.1,
    ringTiltRadians: -0.12,
    ringRadiusX: 1.45,
    ringRadiusY: 0.3,
    markAlpha: 0.4,
    ringAlpha: 0.24
  }
};

export function planetIdentityStyle(bodyId: BodyId | string): PlanetIdentityStyle {
  return PLANET_IDENTITY_STYLES[bodyId as BodyId] ?? PLAIN_PLANET_IDENTITY_STYLE;
}

/**
 * Story 6.5 directional day/night descriptor. `lit` gates whether a directional
 * shading pass applies; `lightX`/`lightY` is the finite unit vector pointing from
 * the lit body TOWARD the Sun (the light source — the caller passes the Sun's real
 * screen position, which is the focus only when the Sun is the centre); `nightShade`
 * is the subtle darkening applied to the anti-sun hemisphere.
 */
export interface TerminatorShading {
  readonly lit: boolean;
  readonly lightX: number;
  readonly lightY: number;
  readonly nightShade: number;
}

export const PLAIN_TERMINATOR_SHADING: TerminatorShading = {
  lit: false,
  lightX: 0,
  lightY: 0,
  nightShade: 0
};

// Night-side darkening strength (alpha of black toward the anti-sun limb). Kept
// subtle so it reads at the ~10 device-px readable radius without swallowing the
// identity colour or the Story 6.4 marks (Design Notes). This is illustrative
// directional lighting, not a physically exact solar-illumination model.
const TERMINATOR_NIGHT_SHADE = 0.32;

/**
 * Pure Story 6.5 helper: given a lit body's screen position, the LIGHT SOURCE's
 * screen position (the Sun — the focus only when the Sun is the centre), and the
 * drawn sphere radius, return a deterministic directional-shading descriptor. The
 * light direction is the unit vector from the body toward the Sun (so the day side
 * faces the Sun). Falls back to the plain unlit descriptor when the direction is
 * degenerate (body coincident with the light source) or the radius is non-positive,
 * so the caller draws a plain Story 6.2 sphere. Derived purely from the
 * already-projected screen positions (AD-10): no second transform, no new position
 * source.
 */
export function terminatorShading(
  body: { x: number; y: number },
  lightSource: { x: number; y: number },
  radius: number
): TerminatorShading {
  if (!(radius > 0)) return PLAIN_TERMINATOR_SHADING;
  const dx = lightSource.x - body.x;
  const dy = lightSource.y - body.y;
  const length = Math.hypot(dx, dy);
  if (!(length > 0) || !Number.isFinite(length)) return PLAIN_TERMINATOR_SHADING;
  return {
    lit: true,
    lightX: dx / length,
    lightY: dy / length,
    nightShade: TERMINATOR_NIGHT_SHADE
  };
}

/**
 * The unit direction the sphere's diffuse highlight and specular glint are offset
 * toward: TOWARD the Sun when the body is sun-lit (the same vector the Story 6.5
 * terminator uses), so the bright side and the little white shimmer track the actual
 * light source instead of sitting at a fixed corner. Falls back to the historical
 * top-left direction (Story 6.2) for the unlit case (the central light source itself
 * and realistic/degenerate bodies), so their settled look is unchanged.
 */
export function sphereLightDirection(terminator: TerminatorShading): { x: number; y: number } {
  if (terminator.lit) return { x: terminator.lightX, y: terminator.lightY };
  return { x: -Math.SQRT1_2, y: -Math.SQRT1_2 };
}

/**
 * Canvas2D baseline adapter (AD-3): the complete Compatibility path. It draws the
 * current-time frame (orbiting bodies at `positionAtTime(body, central mu,
 * state.time)`), reads positions and geometry from core (AD-1, never recomputing
 * orbital math), and uses the one shared projection (AD-10). Decoration
 * (starfield, glow) degrades before correctness (AD-9): orbit lines, markers, and
 * indicators always draw.
 *
 * Reduced-effects is an independent axis that visibly changes THIS baseline too
 * (AC6 / Task 8), not only the WebGL overlay. The effective reduced signal is the
 * union of the OS reduced-motion preference (`hooks.reducedMotion`, threaded in the
 * same way the WebGL adapter receives it) and the in-app toggle
 * (`state.reducedEffects`, read live from the rendered state). When reduced, ONLY
 * decoration is dropped: the starfield is skipped and the additive glow radius is
 * 0. Orbit lines, body markers, the selection ring, and the centre label are never
 * degraded (never degrade information, only decoration — AD-9).
 */
export function createCanvas2dRenderer(
  canvas: HTMLCanvasElement,
  hooks?: RendererHooks
): Renderer {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    // Canvas2D is the guaranteed baseline; a null 2D context is unrecoverable and
    // is surfaced to the composition root rather than silently ignored.
    throw new Error('Canvas 2D context unavailable.');
  }
  const context = ctx;

  // The OS reduced-motion signal captured at construction, exactly as the WebGL
  // adapter receives it; the in-app reduced-effects toggle is read live from state.
  const osReducedMotion = hooks?.reducedMotion === true;

  let cssWidth = canvas.clientWidth || 1366;
  let cssHeight = canvas.clientHeight || 768;
  let dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

  // The projection this adapter last rendered with, exposed via getProjection so
  // the ui pointer hit-test / drag inverse map through the ONE shared transform
  // (AD-10) instead of computing a second one. Null until the first render.
  let lastProjection: Projection | null = null;

  // --- Illustrative collision burst (Story 4.1, Task 8; decoration, AD-9) ---------
  // A SELF-CONTAINED transient effect. The renderer diffs body statuses between
  // frames; when a body transitions active -> removed it captures the last screen
  // position it drew for that body and plays a short decorative burst. This NEVER
  // gates correctness and NEVER feeds the camera fit (AD-9/AD-10): removal + status
  // live in state (the `status === 'active'` filter already drops the body and its
  // orbit). Burst timing is wall-clock, a browser concern allowed in `render`, kept
  // out of `core`/`state`. Under the reduced signal the animated ring is replaced by a
  // single brief static ring (AC 7); removal/status are identical either way.
  const prevStatus = new Map<BodyId, BodyStatus>();
  const lastScreen = new Map<BodyId, { x: number; y: number }>();

  // --- Marching-dashes selection ring (Story 6.1; decoration, AD-9) ---------------
  // The outer dashed selection ring rotates continuously by advancing
  // `lineDashOffset` from the same wall-clock the burst uses (a browser concern kept
  // out of `core`/`state`, AD-1). The dash pattern is [3*dpr, 4*dpr], so one full
  // dash cell is 7*dpr device pixels; a calm speed marches ~one cell every ~0.7s.
  // Under the reduced signal the offset is 0 (static, the prior behaviour, AC-3):
  // only the MOTION degrades, the ring is always fully drawn. The continuous
  // re-render that keeps this advancing WHILE PAUSED is a render-only rAF loop in the
  // composition/host layer (CanvasHost.svelte); during PLAY the sim tick already
  // repaints every frame so the offset advances there for free.
  const SELECTION_DASH_CELL_CSS = 7; // 3 + 4, scaled by dpr at draw time
  const SELECTION_DASH_SPEED_CSS_PER_MS = 0.01; // ~one cell (7px) per 0.7s
  interface Burst {
    x: number;
    y: number;
    colour: string;
    /** Which removal produced this effect: body-body 'collided' vs central 'absorbed'. */
    kind: BodyStatus;
    start: number;
  }
  let bursts: Burst[] = [];
  const BURST_DURATION_MS = 900;
  const nowMs = (): number =>
    typeof performance !== 'undefined' ? performance.now() : Date.now();

  function drawBurst(b: Burst, elapsedFraction: number, reduced: boolean): void {
    // elapsedFraction in [0, 1). Ring fades out over the burst lifetime.
    const alpha = Math.max(0, 1 - elapsedFraction);
    context.save();
    context.globalAlpha = alpha * 0.85;
    context.strokeStyle = b.colour;
    context.setLineDash([]);
    if (b.kind === 'absorbed') {
      // Central absorption (Story 4.2): an INWARD implosion — a ring that CONTRACTS
      // toward the focus (the opposite motion to the collision burst's outward
      // expansion), so the two outcomes read differently. Error colour (reserved for
      // absorption). Reduced signal: a single static ring, no motion (AD-9).
      if (reduced) {
        context.lineWidth = 2 * dpr;
        context.beginPath();
        context.arc(b.x, b.y, 14 * dpr, 0, Math.PI * 2);
        context.stroke();
      } else {
        const r = (46 - elapsedFraction * 42) * dpr; // contracts 46 -> 4
        context.lineWidth = Math.max(1, 3 * dpr * alpha);
        context.beginPath();
        context.arc(b.x, b.y, r, 0, Math.PI * 2);
        context.stroke();
        const flash = context.createRadialGradient(b.x, b.y, 0, b.x, b.y, r);
        flash.addColorStop(0, hexWithAlpha(b.colour, alpha * 0.45));
        flash.addColorStop(1, hexWithAlpha(b.colour, 0));
        context.fillStyle = flash;
        context.beginPath();
        context.arc(b.x, b.y, r, 0, Math.PI * 2);
        context.fill();
      }
    } else if (reduced) {
      // Reduced signal: a single, static (non-expanding), fading ring, no motion.
      context.lineWidth = 2 * dpr;
      context.beginPath();
      context.arc(b.x, b.y, 16 * dpr, 0, Math.PI * 2);
      context.stroke();
    } else {
      // Body-body collision: an expanding faded ring plus a soft inner flash.
      const r = (8 + elapsedFraction * 46) * dpr;
      context.lineWidth = Math.max(1, 3 * dpr * alpha);
      context.beginPath();
      context.arc(b.x, b.y, r, 0, Math.PI * 2);
      context.stroke();
      const flash = context.createRadialGradient(b.x, b.y, 0, b.x, b.y, r);
      flash.addColorStop(0, hexWithAlpha(b.colour, alpha * 0.4));
      flash.addColorStop(1, hexWithAlpha(b.colour, 0));
      context.fillStyle = flash;
      context.beginPath();
      context.arc(b.x, b.y, r, 0, Math.PI * 2);
      context.fill();
    }
    context.restore();
  }

  function applyBackingSize(): void {
    canvas.width = Math.max(1, Math.round(cssWidth * dpr));
    canvas.height = Math.max(1, Math.round(cssHeight * dpr));
  }
  applyBackingSize();

  function drawStarfield(): void {
    // Deterministic faint starfield (decoration, AD-9). Fixed seed so the static
    // frame is reproducible for snapshot evidence.
    const count = 140;
    let seed = 20260706;
    const rand = () => {
      seed = (seed * 1664525 + 1013904223) % 4294967296;
      return seed / 4294967296;
    };
    context.save();
    for (let i = 0; i < count; i++) {
      const x = rand() * canvas.width;
      const y = rand() * canvas.height;
      const r = rand() * 1.1 * dpr + 0.3 * dpr;
      const alpha = 0.05 + rand() * 0.18;
      context.globalAlpha = alpha;
      context.fillStyle = ON_SURFACE;
      context.beginPath();
      context.arc(x, y, r, 0, Math.PI * 2);
      context.fill();
    }
    context.restore();
  }

  function drawOrbit(body: Body, projection: Projection): void {
    const ellipse = orbitEllipse(body);
    const centre = projection.worldToScreen(ellipse.centreX, ellipse.centreY);
    const rx = projection.lengthToScreen(ellipse.semiMajor);
    const ry = projection.lengthToScreen(ellipse.semiMinor);
    if (rx < 0.5 || ry < 0.5) return;
    context.save();
    // Orbit path (Story 6.x clarity fix): the old muted OUTLINE_VARIANT at 0.18
    // alpha was barely visible on the dark field. Use a lighter cool-grey stroke at
    // a higher opacity and a slightly thicker line so the dashed path reads clearly,
    // while staying dimmer than the selection ring (ON_SURFACE) so the hierarchy holds.
    context.strokeStyle = lighten(OUTLINE_VARIANT, 0.5);
    context.globalAlpha = 0.5;
    context.lineWidth = Math.max(1, 1.25 * dpr);
    context.setLineDash([6 * dpr, 6 * dpr]);
    context.beginPath();
    context.ellipse(centre.x, centre.y, rx, ry, 0, 0, Math.PI * 2);
    context.stroke();
    context.restore();
  }

  function drawSectors(
    elements: { a: number; e: number },
    count: number,
    projection: Projection
  ): void {
    // Equal-time swept-area sectors (Story 2.1, FR-9), part of the COMPLETE Canvas2D
    // baseline. Positions come from core (`buildSectors` reuses
    // positionAtMeanAnomaly); the renderer recomputes no orbital math (AD-3) and
    // maps every polygon point through the ONE shared projection (AD-10). The
    // geometry is INFORMATION (Kepler's 2nd law), so it is NEVER gated by the
    // reduced-effects signal (AC 7 / AD-9): only optional embellishment would be.
    const sectors = buildSectors(elements, count);
    context.save();
    context.lineWidth = Math.max(1, dpr);
    for (const sector of sectors) {
      const pts = sector.polygon;
      if (pts.length < 3) continue;
      context.beginPath();
      const first = projection.worldToScreen(pts[0].x, pts[0].y);
      context.moveTo(first.x, first.y);
      for (let i = 1; i < pts.length; i++) {
        const s = projection.worldToScreen(pts[i].x, pts[i].y);
        context.lineTo(s.x, s.y);
      }
      context.closePath();
      // Alternating translucent fills on adjacent sectors; low opacity so the
      // dashed orbit line underneath stays visible (AC 5). A pure helper assigns
      // each sector a fill CLASS so NO two cyclically-adjacent wedges (including the
      // periapsis-seam pair count-1 / 0) share a tone. Even counts use two classes
      // exactly as before; ODD counts get a third class (2) on the last sector,
      // mapped here to a calm mid-alpha PRIMARY so the overlay stays on-brand.
      const cls = sectorFillClass(sector.index, sectors.length);
      const fill = cls === 1 ? PRIMARY_CONTAINER : PRIMARY;
      const alpha = cls === 0 ? 0.18 : cls === 1 ? 0.2 : 0.34;
      context.fillStyle = hexWithAlpha(fill, alpha);
      context.fill();
      // 1px boundary stroke in the same family; the boundaries stay visible through
      // the fill so a screen-reader-independent viewer sees the equal-time division.
      context.strokeStyle = hexWithAlpha(PRIMARY, 0.5);
      context.stroke();
    }
    context.restore();
  }

  function drawFocusRing(x: number, y: number, radius: number): void {
    // The focus (origin) reads as "the point everything is measured from": the same
    // solid + dashed double ring the selection uses (DESIGN.md), in the primary
    // family. Information, not decoration, so it is not reduced-gated.
    context.save();
    context.strokeStyle = PRIMARY;
    context.lineWidth = 2 * dpr;
    context.setLineDash([]);
    context.beginPath();
    context.arc(x, y, radius + 6 * dpr, 0, Math.PI * 2);
    context.stroke();
    context.strokeStyle = ON_SURFACE;
    context.lineWidth = 1 * dpr;
    context.setLineDash([3 * dpr, 4 * dpr]);
    context.beginPath();
    context.arc(x, y, radius + 11 * dpr, 0, Math.PI * 2);
    context.stroke();
    context.restore();
  }

  function drawSphere(
    x: number,
    y: number,
    radius: number,
    colour: string,
    glow: number,
    specular = true,
    identity: PlanetIdentityStyle = PLAIN_PLANET_IDENTITY_STYLE,
    terminator: TerminatorShading = PLAIN_TERMINATOR_SHADING
  ): void {
    context.save();
    if (glow > 0) {
      const glowGrad = context.createRadialGradient(x, y, radius, x, y, radius + glow);
      glowGrad.addColorStop(0, hexWithAlpha(colour, 0.55));
      glowGrad.addColorStop(1, hexWithAlpha(colour, 0));
      context.fillStyle = glowGrad;
      context.beginPath();
      context.arc(x, y, radius + glow, 0, Math.PI * 2);
      context.fill();
    }
    // Dimensional-lighting sphere: bright highlight offset toward the LIGHT (the Sun
    // for a lit orbiting body; the historical top-left for the unlit central/realistic
    // case), deepening to a darkened limb at the edge (Story 6.2, AC-2) so the disc
    // reads as a rounded 3D ball rather than a flat-shaded circle. The 0.49 factor
    // reproduces the original diagonal offset magnitude for the top-left fallback. The
    // identity colour still dominates; only the outer stop is darkened, not repainted.
    const light = sphereLightDirection(terminator);
    const grad = context.createRadialGradient(
      x + light.x * radius * 0.49,
      y + light.y * radius * 0.49,
      radius * 0.1,
      x,
      y,
      radius
    );
    grad.addColorStop(0, lighten(colour, 0.35));
    grad.addColorStop(1, darken(colour, 0.22));
    context.fillStyle = grad;
    context.beginPath();
    context.arc(x, y, radius, 0, Math.PI * 2);
    context.fill();
    // Specular glint (AC-1): a small, soft, partial-opacity highlight toward the SAME
    // light direction as the diffuse highlight above (sun-ward for a lit body), so the
    // little white shimmer always sits on the side facing the Sun rather than a fixed
    // corner. Tuned as a fraction of `radius` so it stays legible (not muddy) at the
    // ~10 device-px radius readable-mode planets are drawn at, and stays subtle (not a
    // hard white dot that would flatten the identity colour, AC-3). The Sun is a light
    // source rather than a lit body, so callers may pass `specular = false` to keep
    // its treatment tasteful instead of pasting a glint onto a glow.
    if (specular) {
      const glintX = x + light.x * radius * 0.56;
      const glintY = y + light.y * radius * 0.56;
      const glintR = Math.max(radius * 0.22, 0.5);
      const glintGrad = context.createRadialGradient(glintX, glintY, 0, glintX, glintY, glintR);
      glintGrad.addColorStop(0, 'rgba(255, 255, 255, 0.6)');
      glintGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
      context.fillStyle = glintGrad;
      context.beginPath();
      context.arc(glintX, glintY, glintR, 0, Math.PI * 2);
      context.fill();
    }
    // Story 6.5 sun-lit day/night terminator: a subtle darkening of the anti-sun
    // hemisphere, layered AFTER the Story 6.2 base fill + specular and clipped to
    // the sphere. Drawn BEFORE the Story 6.4 identity marks so those marks stay
    // legible on top (composed with, not destroyed by, the shade). Only applies to
    // lit orbiting bodies (`terminator.lit`); the central light source and
    // realistic-mode locators pass the plain/unlit descriptor and skip this pass
    // entirely (so no clip fires, preserving scale honesty). Static, not motion:
    // never gated by the reduced signal (it is part of the dimensional model).
    if (terminator.lit && radius > 0) {
      context.save();
      context.beginPath();
      context.arc(x, y, radius, 0, Math.PI * 2);
      context.clip();
      // Linear ramp along the sun-facing axis: transparent at the day limb and
      // through the terminator (mid), deepening to `nightShade` black at the
      // anti-sun limb. The gradient endpoints scale with `radius`, so the shade
      // vanishes with sub-pixel bodies.
      const dayX = x + terminator.lightX * radius;
      const dayY = y + terminator.lightY * radius;
      const nightX = x - terminator.lightX * radius;
      const nightY = y - terminator.lightY * radius;
      const shade = context.createLinearGradient(dayX, dayY, nightX, nightY);
      shade.addColorStop(0, 'rgba(0, 0, 0, 0)');
      shade.addColorStop(0.5, 'rgba(0, 0, 0, 0)');
      shade.addColorStop(1, `rgba(0, 0, 0, ${terminator.nightShade})`);
      context.fillStyle = shade;
      context.beginPath();
      context.arc(x, y, radius, 0, Math.PI * 2);
      context.fill();
      context.restore();
    }
    drawPlanetIdentity(x, y, radius, colour, identity);
    context.restore();
  }

  function drawPlanetIdentity(
    x: number,
    y: number,
    radius: number,
    colour: string,
    style: PlanetIdentityStyle
  ): void {
    if (style.mark === 'plain' || radius <= 0) return;

    if (style.ringRadiusX > 0 && style.ringRadiusY > 0) {
      context.save();
      context.globalAlpha = style.ringAlpha;
      context.strokeStyle = lighten(colour, 0.32);
      context.lineWidth = Math.max(1, radius * 0.18);
      context.setLineDash([]);
      context.beginPath();
      context.ellipse(
        x,
        y,
        radius * style.ringRadiusX,
        radius * style.ringRadiusY,
        style.ringTiltRadians,
        0,
        Math.PI * 2
      );
      context.stroke();
      context.strokeStyle = darken(colour, 0.35);
      context.lineWidth = Math.max(1, radius * 0.08);
      context.beginPath();
      context.ellipse(
        x,
        y,
        radius * style.ringRadiusX * 0.82,
        radius * style.ringRadiusY * 0.7,
        style.ringTiltRadians,
        0,
        Math.PI * 2
      );
      context.stroke();
      context.restore();
    }

    context.save();
    context.beginPath();
    context.arc(x, y, radius * 0.92, 0, Math.PI * 2);
    context.clip();
    context.globalAlpha = style.markAlpha;
    context.setLineDash([]);

    const dark = darken(colour, 0.42);
    const light = lighten(colour, 0.48);

    if (style.mark === 'crater') {
      drawIdentitySpot(x, y, radius, -0.18, -0.05, 0.18, 0.13, 0.15, dark);
      drawIdentitySpot(x, y, radius, 0.28, 0.22, 0.13, 0.1, -0.25, dark);
      drawIdentitySpot(x, y, radius, 0.2, -0.32, 0.1, 0.08, 0, light);
    } else if (style.mark === 'cloud-band') {
      drawIdentityBands(x, y, radius, style, light);
    } else if (style.mark === 'land-patch') {
      drawIdentitySpot(x, y, radius, -0.2, 0.03, 0.26, 0.18, -0.35, 'rgba(72, 166, 112, 1)');
      drawIdentitySpot(x, y, radius, 0.24, -0.2, 0.18, 0.12, 0.25, 'rgba(68, 146, 96, 1)');
    } else if (style.mark === 'dark-patch') {
      drawIdentitySpot(x, y, radius, -0.12, -0.08, 0.3, 0.12, -0.4, dark);
      drawIdentitySpot(x, y, radius, 0.24, 0.2, 0.18, 0.1, 0.2, dark);
    } else if (style.mark === 'bands' || style.mark === 'ringed-bands') {
      drawIdentityBands(x, y, radius, style, dark);
      if (style.spotCount > 0) {
        drawIdentitySpot(x, y, radius, 0.34, 0.13, 0.18, 0.11, -0.12, 'rgba(170, 58, 42, 1)');
      }
    } else if (style.mark === 'tilted-band') {
      drawIdentityBands(x, y, radius, style, light);
    } else if (style.mark === 'storm-spot') {
      drawIdentityBands(x, y, radius, style, light);
      drawIdentitySpot(x, y, radius, 0.24, 0.08, 0.18, 0.12, 0.15, dark);
    }

    context.restore();
  }

  function drawIdentityBands(
    x: number,
    y: number,
    radius: number,
    style: PlanetIdentityStyle,
    colour: string
  ): void {
    const count = Math.max(1, style.bandCount);
    const spread = count === 1 ? 0 : 0.52;
    context.strokeStyle = colour;
    context.lineWidth = Math.max(1, radius * 0.14);
    for (let i = 0; i < count; i++) {
      const t = count === 1 ? 0 : i / (count - 1) - 0.5;
      drawIdentityBand(x, y, radius, style.bandTiltRadians, t * spread);
    }
  }

  function drawIdentityBand(
    x: number,
    y: number,
    radius: number,
    angle: number,
    offset: number
  ): void {
    const dx = Math.cos(angle) * radius * 0.82;
    const dy = Math.sin(angle) * radius * 0.82;
    const nx = -Math.sin(angle) * radius * offset;
    const ny = Math.cos(angle) * radius * offset;
    context.beginPath();
    context.moveTo(x + nx - dx, y + ny - dy);
    context.lineTo(x + nx + dx, y + ny + dy);
    context.stroke();
  }

  function drawIdentitySpot(
    x: number,
    y: number,
    radius: number,
    cx: number,
    cy: number,
    rx: number,
    ry: number,
    rotation: number,
    colour: string
  ): void {
    context.fillStyle = colour;
    context.beginPath();
    context.ellipse(
      x + radius * cx,
      y + radius * cy,
      Math.max(0.8 * dpr, radius * rx),
      Math.max(0.6 * dpr, radius * ry),
      rotation,
      0,
      Math.PI * 2
    );
    context.fill();
  }

  function drawLocator(x: number, y: number, colour: string): void {
    // Fixed-size accessible position marker for realistic mode (Story 3.3, AC3 /
    // AD-8): a hollow ring plus a crosshair, SHAPE-distinct from the filled
    // `drawSphere` disc so it reads as a locator, not the body's size. Its size is a
    // fixed device-pixel constant, decoupled from physicalRadius and collisionRadius,
    // so it never misrepresents either. It carries meaning by shape, not colour
    // alone; the identity colour merely strokes it. INFORMATION, never reduced-gated
    // (AD-9): drawn regardless of the reduced-effects signal.
    const r = REALISTIC_LOCATOR_RADIUS_CSS * dpr;
    context.save();
    context.strokeStyle = colour;
    context.lineWidth = Math.max(1, 1.5 * dpr);
    context.setLineDash([]);
    // Hollow ring.
    context.beginPath();
    context.arc(x, y, r, 0, Math.PI * 2);
    context.stroke();
    // Crosshair ticks extending just beyond the ring.
    const tick = r + 3 * dpr;
    context.beginPath();
    context.moveTo(x - tick, y);
    context.lineTo(x - r, y);
    context.moveTo(x + r, y);
    context.lineTo(x + tick, y);
    context.moveTo(x, y - tick);
    context.lineTo(x, y - r);
    context.moveTo(x, y + r);
    context.lineTo(x, y + tick);
    context.stroke();
    context.restore();
  }

  function drawCentreLabel(x: number, y: number, radius: number, text: string): void {
    context.save();
    const fontPx = 12 * dpr;
    context.font = `700 ${fontPx}px Inter, system-ui, sans-serif`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    const paddingX = 8 * dpr;
    const paddingY = 5 * dpr;
    const metrics = context.measureText(text);
    const boxW = metrics.width + paddingX * 2;
    const boxH = fontPx + paddingY * 2;
    const boxX = x - boxW / 2;
    const boxY = y + radius + 8 * dpr;
    // Dark translucent pill background for the on-canvas label.
    context.fillStyle = 'rgba(12, 14, 18, 0.85)';
    roundRect(context, boxX, boxY, boxW, boxH, boxH / 2);
    context.fill();
    context.fillStyle = ON_SURFACE;
    context.fillText(text, x, boxY + boxH / 2 + 0.5 * dpr);
    context.restore();
  }

  function drawSelectionRing(
    x: number,
    y: number,
    radius: number,
    colour: string,
    dashOffset: number
  ): void {
    // Non-colour-only selection signal: two concentric rings of distinct style.
    context.save();
    // Inner solid ring in the selected body's identity colour.
    context.strokeStyle = colour;
    context.lineWidth = 2 * dpr;
    context.beginPath();
    context.arc(x, y, radius + 5 * dpr, 0, Math.PI * 2);
    context.stroke();
    // Outer white dashed ring. `dashOffset` marches the dashes when motion is
    // allowed; it is 0 under the reduced signal so the ring is static (AC-3).
    context.strokeStyle = ON_SURFACE;
    context.lineWidth = 1 * dpr;
    context.setLineDash([3 * dpr, 4 * dpr]);
    context.lineDashOffset = dashOffset;
    context.beginPath();
    context.arc(x, y, radius + 10 * dpr, 0, Math.PI * 2);
    context.stroke();
    context.restore();
  }

  function drawGrabAffordance(x: number, y: number, radius: number): void {
    // Grabbed-body cue carried by SHAPE (a square bracket frame + corner ticks),
    // distinct from the round selection ring and the centre label, so it reads
    // without colour and survives reduced-motion (a state cue, not decoration).
    context.save();
    const r = radius + 12 * dpr;
    context.strokeStyle = ON_SURFACE;
    context.lineWidth = 2 * dpr;
    context.setLineDash([]);
    // Four corner brackets forming a square target around the grabbed body.
    const tick = 7 * dpr;
    const corners: Array<[number, number, number, number]> = [
      [-1, -1, 1, 0],
      [-1, -1, 0, 1],
      [1, -1, -1, 0],
      [1, -1, 0, 1],
      [-1, 1, 1, 0],
      [-1, 1, 0, -1],
      [1, 1, -1, 0],
      [1, 1, 0, -1]
    ];
    context.beginPath();
    for (const [cx, cy, dx, dy] of corners) {
      const sx = x + cx * r;
      const sy = y + cy * r;
      context.moveTo(sx, sy);
      context.lineTo(sx + dx * tick, sy + dy * tick);
    }
    context.stroke();
    context.restore();
  }

  function drawDropTargetLabel(x: number, y: number, radius: number): void {
    // Drop-on-centre target: a text label (not colour alone) near the centre so
    // its meaning is explicit. Absent at rest; shown only while over the centre.
    drawCentreLabel(x, y, radius, 'DROP TO SET AS CENTRE');
  }

  function render(state: AppState, dragOverlay?: DragOverlay | null): void {
    // Effective reduced signal: the union of the OS reduced-motion preference and
    // the in-app reduced-effects toggle (Consistency Conventions), the SAME union
    // the WebGL adapter uses. Gates decoration only (AD-9).
    const reduced = osReducedMotion || state.reducedEffects;

    // Marching-dashes offset for the selection ring (Story 6.1). Time-derived when
    // motion is allowed so the dashes rotate; 0 under the reduced signal (static,
    // AC-3). Negative so the dashes travel clockwise. Read once per frame so every
    // ring in the frame is in phase. The wall-clock read is a render-only browser
    // concern (AD-1); it never touches `core`/`state` or `state.time`.
    const selectionDashOffset = reduced
      ? 0
      : -(
          (nowMs() * SELECTION_DASH_SPEED_CSS_PER_MS * dpr) %
          (SELECTION_DASH_CELL_CSS * dpr)
        );

    const central = getBody(state.centralBody);
    // Geometry reads each body's LIVE a/e from the store (single source of truth,
    // Story 1.3), never the fixed reference e; identity colour still comes from
    // reference data. An eccentricity edit therefore repaints immediately.
    // The `status === 'active'` filter matches the WebGL adapter so both feed the
    // one shared projection identical camera-fit inputs (Finding 2); Epic 4 will
    // decide the camera semantics for removed (collided/absorbed) bodies. All
    // bodies are active today, so this is no visible change now.
    const orbiting: Body[] = state.bodies
      .filter((b) => b.id !== state.centralBody && b.status === 'active')
      .map((b) => ({ ...getBody(b.id), a: b.a, e: b.e }));

    const viewport = { cssWidth, cssHeight, dpr };
    // The ONE camera-composition site (AD-10, Story 3.2): fold the session-only
    // zoom/pan modifier over the always-recomputed fit baseline. Both adapters and
    // the ui hit-test/drag inverse consume this identical projection.
    const camera = composeCamera(
      viewport,
      maxApoapsis(orbiting),
      state.camera.zoom,
      state.camera.panX,
      state.camera.panY,
      SAFE_INSET_CSS
    );
    const projection = createProjection(viewport, camera);
    // Publish for the ui hit-test / drag inverse (AD-10). Phase offsets do not
    // change the camera-fit inputs, so this is the same transform in every frame.
    lastProjection = projection;

    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = SURFACE_LOWEST;
    context.fillRect(0, 0, canvas.width, canvas.height);
    // Decoration: the starfield is skipped under the effective reduced signal.
    if (!reduced) drawStarfield();

    // Orbit paths (drawn under bodies, always visible).
    for (const body of orbiting) {
      drawOrbit(body, projection);
    }

    // Equal-time-sector overlay for the SELECTED orbit (Story 2.1, FR-9). The
    // predicate mirrors the `selectSectorSettings.applicable` selector exactly
    // (visible AND selected is a non-central ACTIVE orbiter) so the canvas and the
    // detail-panel N/A state never disagree. Built from the selected body's LIVE
    // a/e (read from the store body, as drawOrbit reads live a/e) so an
    // eccentricity or semi-major-axis edit reconstructs the overlay every frame
    // (AC 6). Drawn over the orbit lines but under the bodies; NEVER reduced-gated
    // (AC 7): the geometry is information, not decoration.
    const selectedBody = state.bodies.find((b) => b.id === state.selectedBody);
    const drawSectorOverlay =
      state.sectorOverlayVisible &&
      state.selectedBody !== state.centralBody &&
      selectedBody !== undefined &&
      selectedBody.status === 'active';
    if (drawSectorOverlay && selectedBody) {
      drawSectors({ a: selectedBody.a, e: selectedBody.e }, state.sectorCount, projection);
    }

    // Body-marker radii come from the ONE shared helper (Story 3.3, AD-10):
    // readable uses the fixed exaggerated sizes; realistic maps the true
    // `physicalRadius` through the SAME projection length scale as the orbital
    // distances, so planets are sub-pixel by design (the teaching contrast, not a
    // bug). The marker radius NEVER feeds `maxApoapsis` or the camera, so distances
    // are identical across modes (AC2).
    const realistic = state.scaleMode === 'realistic';
    const locatorAnchor = REALISTIC_LOCATOR_RADIUS_CSS * dpr;

    // Central body at the focus (origin), strongest glow.
    const centreScreen = projection.worldToScreen(0, 0);
    // The Sun is ALWAYS the light source, wherever it is — not merely "the centre".
    // When the Sun is the central body it sits at the focus; when another body is
    // swapped to the centre (the experimental view) the Sun is an orbiter, so the
    // lit hemisphere / specular glint of every body must track the Sun's real
    // projected position, not the focus. Falls back to the focus if the Sun has been
    // removed (absorbed/collided as an orbiter) so lighting stays defined.
    let sunScreen = centreScreen;
    if (state.centralBody !== 'sun') {
      const sunOrbiter = orbiting.find((b) => b.id === 'sun');
      if (sunOrbiter) {
        const sunOffset = state.phaseOffsets['sun'] ?? 0;
        const sunPos = effectiveWorldPosition(sunOrbiter, central.mu, state.time, sunOffset);
        sunScreen = projection.worldToScreen(sunPos.x, sunPos.y);
      }
    }
    const centralIsSun = central.id === 'sun';
    // Only the SUN gets the dedicated (larger, boot-framing-safe) central marker size.
    // A planet swapped to the centre keeps its OWN physical-size-ladder marker, so it
    // does not balloon to the Sun's size — relative body sizes stay honest.
    const centralRadius = markerRadiusPx(
      state.scaleMode,
      central.physicalRadius,
      centralIsSun,
      projection,
      dpr,
      state.camera.zoom
    );
    // Anchor for label / focus ring so they do not collapse onto a sub-pixel point
    // in realistic mode; the drawn SPHERE still uses the true `centralRadius` so the
    // true-ratio anchor is preserved (AC2).
    const centralAnchor = realistic ? Math.max(centralRadius, locatorAnchor) : centralRadius;
    // Glow is decoration (AD-9): suppressed under the reduced signal AND in realistic
    // mode, where a fixed-size halo would misrepresent the body's true tiny size
    // (AC3 / NFR24).
    // The Sun is a light source, not a lit body: as the centre it keeps the diffuse
    // highlight and limb (tasteful with its bright gradient + glow) but skips the
    // specular glint and day/night terminator. A NON-Sun central body (the swapped
    // experimental view) is a lit body, so it gets a glint and terminator pointing at
    // the Sun's real position like any orbiter.
    const centralTerminator =
      realistic || centralIsSun
        ? PLAIN_TERMINATOR_SHADING
        : terminatorShading(centreScreen, sunScreen, centralRadius);
    drawSphere(
      centreScreen.x,
      centreScreen.y,
      centralRadius,
      central.colour,
      // The Sun always glows the most; a non-Sun central body glows like a planet.
      reduced || realistic ? 0 : (centralIsSun ? 28 : 6) * dpr,
      !centralIsSun && !realistic,
      realistic ? PLAIN_PLANET_IDENTITY_STYLE : planetIdentityStyle(central.id),
      centralTerminator
    );
    // Fixed-size accessible locator in realistic mode (information, never
    // reduced-gated, AD-9): keeps the sub-pixel central body perceivable.
    if (realistic) {
      drawLocator(centreScreen.x, centreScreen.y, central.colour);
    }
    // While a dragged body is over the centre, show the drop-target label instead
    // of the standard centre label so the target's meaning is explicit (AC 9).
    if (dragOverlay?.overCentre) {
      drawDropTargetLabel(centreScreen.x, centreScreen.y, centralAnchor);
    } else {
      drawCentreLabel(
        centreScreen.x,
        centreScreen.y,
        centralAnchor,
        `${central.name.toUpperCase()} (CENTRE)`
      );
    }
    // Focus marker: the point the sectors are measured from (Story 2.1). Drawn over
    // the central sphere so it reads clearly; information, not reduced-gated.
    if (drawSectorOverlay) {
      drawFocusRing(centreScreen.x, centreScreen.y, centralAnchor);
    }

    // Orbiting bodies at their current effective phase (Story 1.2 motion + Story
    // 1.7 dragged offset), solved from core against the central body's fixed mu
    // via the shared effective-position path (AD-3: the renderer reads positions,
    // never recomputes Kepler's equation itself). At time zero, offset zero, this
    // is periapsis.
    for (const body of orbiting) {
      const offset = state.phaseOffsets[body.id] ?? 0;
      const pos = effectiveWorldPosition(body, central.mu, state.time, offset);
      const screen = projection.worldToScreen(pos.x, pos.y);
      // Remember where each active body was last drawn, so a burst can be spawned at
      // its final position when it is removed next frame (decoration, AD-9).
      lastScreen.set(body.id, { x: screen.x, y: screen.y });
      // Per-body marker radius from the shared helper (readable fixed size vs
      // realistic true `physicalRadius`). Glow suppressed in realistic mode (AC3).
      const planetRadius = markerRadiusPx(state.scaleMode, body.physicalRadius, false, projection, dpr, state.camera.zoom);
      // Anchor for rings / grab affordance so they never collapse onto a sub-pixel
      // point; the drawn sphere keeps the true radius (true-ratio anchor, AC2).
      const planetAnchor = realistic ? Math.max(planetRadius, locatorAnchor) : planetRadius;
      // Sun-lit day/night terminator (Story 6.5): light direction is the real
      // projected direction from THIS body toward the SUN (the light source), derived
      // in screen space from the ONE shared projection (AD-10) — so it stays correct
      // even when the Sun is a swapped-out orbiter rather than the centre. The Sun
      // itself is the light source, so it takes no terminator or glint. Realistic
      // mode passes the plain/unlit descriptor for the same scale-honesty reason
      // identity marks are suppressed there (no fixed-size directional artwork on
      // sub-pixel bodies); the helper also self-suppresses for degenerate direction
      // or non-positive radius.
      const bodyIsSun = body.id === 'sun';
      const terminator =
        realistic || bodyIsSun
          ? PLAIN_TERMINATOR_SHADING
          : terminatorShading(screen, sunScreen, planetRadius);
      drawSphere(
        screen.x,
        screen.y,
        planetRadius,
        body.colour,
        // A swapped-out Sun keeps its strongest glow; other orbiters glow softly.
        reduced || realistic ? 0 : (bodyIsSun ? 28 : 6) * dpr,
        !bodyIsSun,
        realistic ? PLAIN_PLANET_IDENTITY_STYLE : planetIdentityStyle(body.id),
        terminator
      );
      // Fixed-size accessible locator in realistic mode (information, AD-9): keeps a
      // sub-pixel body perceivable and selectable without inflating the sprite.
      if (realistic) {
        drawLocator(screen.x, screen.y, body.colour);
      }
      if (body.id === state.selectedBody) {
        drawSelectionRing(screen.x, screen.y, planetAnchor, body.colour, selectionDashOffset);
      }
      // Grabbed-body affordance for the actively dragged body (AC 9), shape-based
      // and reduced-motion safe.
      if (dragOverlay && dragOverlay.bodyId === body.id) {
        drawGrabAffordance(screen.x, screen.y, planetAnchor);
      }
    }

    // Collision burst (Story 4.1, Task 8; decoration only). Diff statuses: an
    // active -> removed transition spawns a burst at the body's LAST drawn screen
    // position (it is filtered out of `orbiting` this frame, so `lastScreen` holds its
    // final active spot). This block is strictly INERT when nothing was removed and no
    // burst is active, so the default (no-collision) frame is byte-identical.
    for (const b of state.bodies) {
      const prev = prevStatus.get(b.id);
      if (prev === 'active' && b.status !== 'active') {
        const at = lastScreen.get(b.id);
        // Absorption uses the reserved error colour and an inward implosion; a
        // body-body collision keeps the removed body's identity colour and expands.
        const colour = b.status === 'absorbed' ? ERROR : getBody(b.id).colour;
        if (at) bursts.push({ x: at.x, y: at.y, colour, kind: b.status, start: nowMs() });
      }
      prevStatus.set(b.id, b.status);
    }
    if (bursts.length > 0) {
      const t = nowMs();
      bursts = bursts.filter((b) => t - b.start < BURST_DURATION_MS);
      for (const b of bursts) {
        drawBurst(b, (t - b.start) / BURST_DURATION_MS, reduced);
      }
    }
  }

  return {
    render,
    getProjection(): Projection | null {
      return lastProjection;
    },
    resize(nextCssWidth: number, nextCssHeight: number, nextDpr: number) {
      cssWidth = nextCssWidth > 0 ? nextCssWidth : cssWidth;
      cssHeight = nextCssHeight > 0 ? nextCssHeight : cssHeight;
      dpr = nextDpr > 0 ? nextDpr : dpr;
      applyBackingSize();
    },
    destroy() {
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);
    }
  };
}

// --- small colour + path helpers (presentation only) ---

function clamp255(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function parseHex(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16)
  };
}

function hexWithAlpha(hex: string, alpha: number): string {
  const { r, g, b } = parseHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function lighten(hex: string, amount: number): string {
  const { r, g, b } = parseHex(hex);
  return `rgb(${clamp255(r + (255 - r) * amount)}, ${clamp255(
    g + (255 - g) * amount
  )}, ${clamp255(b + (255 - b) * amount)})`;
}

// Sibling to `lighten` (Story 6.2, AC-6): darkens a hex colour toward black by
// `amount` (0..1), used for the sphere's limb/rim shading. Pure and exported so
// its channel math is unit-testable without a canvas context.
export function darken(hex: string, amount: number): string {
  const { r, g, b } = parseHex(hex);
  return `rgb(${clamp255(r * (1 - amount))}, ${clamp255(g * (1 - amount))}, ${clamp255(
    b * (1 - amount)
  )})`;
}

function roundRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  const radius = Math.min(r, w / 2, h / 2);
  context.beginPath();
  context.moveTo(x + radius, y);
  context.arcTo(x + w, y, x + w, y + h, radius);
  context.arcTo(x + w, y + h, x, y + h, radius);
  context.arcTo(x, y + h, x, y, radius);
  context.arcTo(x, y, x + w, y, radius);
  context.closePath();
}
